import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SchoolSettingsDto } from '@motoschool/shared';
import { LESSON_MINUTES_OPTIONS } from '@motoschool/shared';
import { api, ApiRequestError } from '../lib/api';
import {
  Button,
  Card,
  ErrorNotice,
  Field,
  Input,
  PageLoader,
  RadioGroup,
  SectionTitle,
  SuccessNotice,
  Switch,
} from '../components/ui';
import { lessonDurationLabel } from '../lib/useTeachers';

interface SettingsResponse {
  settings: SchoolSettingsDto;
}

/**
 * Настройки школы. Отсюда берутся название и контакты, которые видит гость
 * на главной: пока они пустые, звонить со страницы некуда.
 *
 * Telegram школы правится только здесь — на главной его сейчас нет, поэтому
 * и поля нет: показывать в админке то, что никуда не выводится, значит снова
 * развести настройку и сайт.
 */
export function SettingsPage(): React.JSX.Element {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'settings'],
    queryFn: () => api.get<SettingsResponse>('/admin/settings'),
  });

  const [form, setForm] = useState({
    name: '',
    contactPhone: '',
    contactInstagram: '',
    defaultLessonMinutes: LESSON_MINUTES_OPTIONS[0] as number,
    isBookingOpen: true,
  });
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isSaved, setIsSaved] = useState(false);

  // Форма заполняется, когда ответ пришёл: до этого показываем загрузку.
  useEffect(() => {
    if (!data) return;
    setForm({
      name: data.settings.name,
      contactPhone: data.settings.contactPhone ?? '',
      contactInstagram: data.settings.contactInstagram ?? '',
      defaultLessonMinutes: data.settings.defaultLessonMinutes,
      isBookingOpen: data.settings.isBookingOpen,
    });
  }, [data]);

  const save = useMutation({
    mutationFn: () =>
      api.patch<SettingsResponse>('/admin/settings', {
        name: form.name.trim(),
        // Пустое поле — это «контакта нет», а не пустая строка на странице.
        contactPhone: form.contactPhone.trim() || null,
        // «Собаку» и ссылку целиком вводят по привычке — сохраняем одно имя
        contactInstagram:
          form.contactInstagram
            .trim()
            .replace(/^@/, '')
            .replace(/^https?:\/\/(www\.)?instagram\.com\//, '')
            .replace(/\/+$/, '') || null,
        defaultLessonMinutes: form.defaultLessonMinutes,
        isBookingOpen: form.isBookingOpen,
      }),
    onSuccess: async () => {
      setError(null);
      setFieldErrors({});
      setIsSaved(true);
      await queryClient.invalidateQueries({ queryKey: ['admin', 'settings'] });
      // Название и контакты видны гостю — публичный кеш тоже устарел.
      await queryClient.invalidateQueries({ queryKey: ['public'] });
      setTimeout(() => setIsSaved(false), 2500);
    },
    onError: (caught: unknown) => {
      if (caught instanceof ApiRequestError) {
        setError(caught.message);
        setFieldErrors(caught.fieldErrors);
        return;
      }
      setError('Не удалось сохранить настройки');
    },
  });

  if (isLoading) return <PageLoader />;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold">Настройки школы</h1>
        <p className="text-sm text-muted">Название и контакты видны всем на главной странице</p>
      </div>

      {error && <ErrorNotice message={error} />}
      {isSaved && <SuccessNotice>Настройки сохранены</SuccessNotice>}

      <Card className="space-y-4">
        <Field label="Название школы" error={fieldErrors.name} required>
          <Input
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
            placeholder="FREEDOM BIKER"
          />
        </Field>

        <Field
          label="Телефон"
          hint="С главной по нему звонят и пишут в WhatsApp"
          error={fieldErrors.contactPhone}
        >
          <Input
            value={form.contactPhone}
            onChange={(event) => setForm({ ...form, contactPhone: event.target.value })}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="+7 701 000-00-00"
          />
        </Field>

        <Field
          label="Instagram"
          hint="Без «собаки». Отсюда берётся ссылка в окне «Связаться»"
          error={fieldErrors.contactInstagram}
        >
          <Input
            value={form.contactInstagram}
            onChange={(event) => setForm({ ...form, contactInstagram: event.target.value })}
            placeholder="freedom_biker_motoschool"
          />
        </Field>
      </Card>

      <section>
        <SectionTitle>Запись через сайт</SectionTitle>
        <Card className="space-y-3">
          <Switch
            isOn={form.isBookingOpen}
            label="Гость сам выбирает время"
            hint="На главной появится кнопка «Выбрать время», а гость увидит свободные часы и сможет оставить заявку"
            onChange={(isBookingOpen) => setForm({ ...form, isBookingOpen })}
          />

          <p className="text-sm text-muted">
            {form.isBookingOpen
              ? 'Заявки с сайта приходят в раздел «Заявки». Личные ссылки инструкторов работают всегда.'
              : 'Сейчас на главной только контакты: время согласуете сами. Личные ссылки инструкторов продолжают работать.'}
          </p>
        </Card>
      </section>

      <section>
        <SectionTitle>Занятия</SectionTitle>
        <Card className="space-y-3">
          <Field
            label="Длительность по умолчанию"
            hint="Она предложена ученику первой — выбрать другую он всё равно сможет"
          >
            <RadioGroup
              name="Длительность по умолчанию"
              value={form.defaultLessonMinutes}
              options={LESSON_MINUTES_OPTIONS.map((minutes) => ({
                value: minutes,
                label: lessonDurationLabel(minutes),
              }))}
              onChange={(value) => setForm({ ...form, defaultLessonMinutes: value })}
            />
          </Field>

          <p className="text-sm text-muted">
            Часы работы у каждого инструктора свои — они задаются в его карточке
            в разделе «Сотрудники».
          </p>
        </Card>
      </section>

      <Button fullWidth isLoading={save.isPending} onClick={() => save.mutate()}>
        Сохранить
      </Button>
    </div>
  );
}
